import { gql } from '@apollo/client';
import type { ApolloClient } from '@apollo/client';

import GET_SWITCHES_FOR_ACCOUNT from '../gql/getSwitchesForAccount.gql';
import { DEFAULT_LOOKUP_SIZE, getOldest } from '@/lib/oldest';

export const CREATE_SWITCH_FOR_ACCOUNT = gql`
  mutation CreateSwitchForAccount($accountId: ID!, $input: CreateSwitchForAccountInput!) {
    createSwitchForAccount(accountId: $accountId, input: $input) {
      switch {
        id
      }
    }
  }
`;

// New switches start with no forwarding: the NONE protocol (what the Forwarding
// tab saves for "No forwarding"), no endpoints, and SMS/MMS disabled, since the
// backend rejects enabled switches without a real endpoint.
export const defaultSwitchInput = (accountName: string) => ({
  name: `${accountName} Switch`,
  notes: '',
  smsEnabled: false,
  smsProtocol: 'NONE',
  smsMessageFormat: 'MERCURY_SMS_1_0',
  smsEndpoints: [],
  mmsEnabled: false,
  mmsProtocol: 'NONE',
  mmsMessageFormat: 'MERCURY_MMS_1_0',
  mmsEndpoints: [],
});

/**
 * Create an account's `"<Account> Switch"` with forwarding off. Every account
 * gets one when it is created (like its "Account Key"); being the oldest, it
 * becomes the account's default switch. Throws if the switch isn't created.
 */
export const createDefaultSwitch = async (
  client: ApolloClient,
  { accountId, accountName }: { accountId: string; accountName: string }
): Promise<string> => {
  const result = await client.mutate({
    mutation: CREATE_SWITCH_FOR_ACCOUNT,
    variables: { accountId, input: defaultSwitchInput(accountName) },
  });
  const created = (result.data as { createSwitchForAccount: { switch: { id: string } } | null })
    ?.createSwitchForAccount?.switch?.id;
  if (!created) throw new Error('Switch creation failed');

  return created;
};

/**
 * The switch a newly assigned number should use: the one picked, otherwise the
 * account's default (oldest). A switch is only created when the account has none.
 */
export const resolveSwitchId = async (
  client: ApolloClient,
  {
    accountId,
    accountName,
    switchId,
  }: { accountId: string; accountName: string; switchId?: string | null }
): Promise<string> => {
  if (switchId) return switchId;

  const { data } = await client.query({
    query: GET_SWITCHES_FOR_ACCOUNT,
    variables: { id: accountId, first: DEFAULT_LOOKUP_SIZE },
    fetchPolicy: 'network-only',
  });
  const existing = getOldest(data?.switchesForAccount?.edges.map(({ node }) => node) ?? [])?.id;
  if (existing) return existing;

  return createDefaultSwitch(client, { accountId, accountName });
};
